import './styles.scss';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import { isArchived } from '@utils/course';
import { getTextAboveEnrollButton } from '@utils/courseRunSelection';
import { withLocation } from '@utils/navigation';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import EmailOptIn from '@prospectus/common/ui/EmailOptIn';
import EnrollButtonCommon from './EnrollButtonCommon';
import RetirementBanner from '../RetirementBanner';

const getNumberEnrolledFormatting = (enrollmentCount) => ({
  numberEnrolled: enrollmentCount.toLocaleString(),
  markup: (chunks) => <span className="font-weight-bold">{chunks}</span>,
});

// If you change the enroll button state logic, please update ./enroll_button_logic.md
// and https://2u-internal.atlassian.net/wiki/spaces/WS/pages/8749811/Enroll+button+and+Course+Run+Selector+Logic
export function BaseEnrollButton({
  product,
  activeCourseRun,
  enrollmentCount,
  title,
  owners: partners,
  cohesionVariant,
  cohesionCategory,
  cohesionName,
  brandingCTABaseline,
  intl,
  retiringSoon,
  refProp,
  uuid,
  enrolledInOneRun,
}) {
  const [emailOptIn, setEmailOptIn] = useState(false);

  const wrapperRef = useRef(null);

  const trackEmailOptIn = (optIn) => {
    const { key } = activeCourseRun;

    window.analytics.track(
      `edx.bi.user.${product}-details.toggle-email-opt-in`,
      {
        category: `${product}-details`,
        label: key,
        email_opt_in: optIn,
      },
    );
  };

  const toggleEmailOptIn = () => {
    setEmailOptIn(!emailOptIn);
    trackEmailOptIn(!emailOptIn);
  };

  // Only show the enrollment count on non-archived courses with more than 5000 enrolls
  const showEnrollmentCount = () => !isArchived(activeCourseRun) && enrollmentCount > 5000;

  const { isEnrollable: courseIsEnrollable } = activeCourseRun;

  return (
    <div ref={wrapperRef}>
      <div className="font-weight-bold mb-3">
        {getTextAboveEnrollButton(activeCourseRun, intl)}
      </div>
      <div
        ref={refProp}
        tabIndex="-1"
        className={classNames(
          'main-enroll-btn', // added for the "Main Enroll Button" click event on optimizely. can removed if that event is no longer being used.
          'd-flex',
          'align-items-center',
          'flex-lg-row',
          { 'branding-cta-test': brandingCTABaseline },
        )}
      >
        <EnrollButtonCommon
          emailOptIn={emailOptIn}
          uuid={uuid}
          enrolledInOneRun={enrolledInOneRun}
          owners={partners}
          cohesionVariant={cohesionVariant}
          cohesionCategory={cohesionCategory}
          cohesionName={cohesionName}
        />
        {showEnrollmentCount() && (
          <div className="small ml-2">
            <FormattedMessage
              id="prospectus.enrollButton.label.numberEnrolled"
              description="Number of learners enrolled in all of the course runs of this course."
              defaultMessage="<markup>{numberEnrolled}</markup> already enrolled!"
              values={getNumberEnrolledFormatting(enrollmentCount)}
            />
          </div>
        )}
      </div>

      {retiringSoon && (
        <div className="mt-2">
          <RetirementBanner />
        </div>
      )}

      <div className="d-flex flex-column mt-3">
        {courseIsEnrollable && (
          <EmailOptIn
            optIn={emailOptIn}
            partners={partners}
            title={title}
            toggleEmailOptIn={toggleEmailOptIn}
          />
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (store) => ({
  activeCourseRun: store.activeCourseRun,
});

BaseEnrollButton.propTypes = {
  uuid: PropTypes.string.isRequired,
  enrollmentCount: PropTypes.number,
  title: PropTypes.string.isRequired,
  product: PropTypes.string,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  brandingCTABaseline: PropTypes.bool,
  intl: intlShape.isRequired,
  refProp: PropTypes.shape(),
  retiringSoon: PropTypes.bool,
  enrolledInOneRun: PropTypes.bool,
  cohesionVariant: PropTypes.string,
  cohesionCategory: PropTypes.string,
  cohesionName: PropTypes.string,
};

BaseEnrollButton.defaultProps = {
  enrollmentCount: undefined,
  product: 'course',
  brandingCTABaseline: false,
  refProp: React.createRef(),
  retiringSoon: false,
  enrolledInOneRun: false,
  cohesionVariant: null,
  cohesionCategory: null,
  cohesionName: null,
};

const EnrollButton = connect(mapStateToProps)(
  withLocation(injectIntl(BaseEnrollButton)),
);

export default EnrollButton;
